import { useState } from 'react'
import type { UserProfile, TeamMember } from '@/types/user'

interface Props {
  seller: UserProfile | null
  className?: string
}

const VISIBLE_LIMIT = 3

export function AgentContactStack({ seller, className }: Props) {
  const [expanded, setExpanded] = useState(false)

  if (!seller) return null
  const tier = seller.subscriptionTier
  if (tier !== 'storefront' && tier !== 'dealership') return null

  const agents = (seller.teamMembers ?? []).filter((m) => m.isAgent === true)
  if (agents.length === 0) return null

  const visible = expanded ? agents : agents.slice(0, VISIBLE_LIMIT)
  const remainderCount = agents.length - VISIBLE_LIMIT

  return (
    <div className={`space-y-3${className ? ` ${className}` : ''}`}>
      {visible.map((agent) => (
        <AgentCard key={agent.id} agent={agent} />
      ))}
      {!expanded && remainderCount > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="w-full card p-3 text-sm text-primary-600 hover:bg-slate-50 transition-colors"
        >
          Show all ({agents.length})
        </button>
      )}
    </div>
  )
}

function AgentCard({ agent }: { agent: TeamMember }) {
  return (
    <div className="card p-4 flex items-center gap-3">
      <div className="w-10 h-10 rounded-full bg-slate-200 text-slate-500 flex items-center justify-center text-sm font-bold flex-shrink-0 overflow-hidden">
        {agent.photoUrl ? (
          <img src={agent.photoUrl} alt={agent.name} className="w-full h-full object-cover" />
        ) : (
          agent.name[0]?.toUpperCase()
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm truncate">{agent.name}</p>
        {agent.role && <p className="text-xs text-slate-500 truncate">{agent.role}</p>}
      </div>
      <div className="flex gap-2 flex-shrink-0">
        {agent.phone && (
          <a
            href={`tel:${agent.phone}`}
            className="text-xs text-primary-600 hover:underline"
            aria-label={`Call ${agent.name}`}
          >
            {agent.phone}
          </a>
        )}
        {agent.email && (
          <a
            href={`mailto:${agent.email}`}
            className="text-xs text-primary-600 hover:underline"
            aria-label={`Email ${agent.name}`}
          >
            Email
          </a>
        )}
      </div>
    </div>
  )
}
